import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, IconButton, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"

import {
  getOrganizationsEnterpriseSelector,
  getOrganizationsEnterprisesSelector,
  getCatalogProductTypesSelector,
} from "../../../../reducers/Organizations/organizations"
import { ACTIVE_KFC_BRANDS, COLORS } from "../../../../utils"
import { removeCatalogSource } from "../../../../actions/Organizations/Enterprise"
import CatalogSourceModal from "./CatalogSourceModal"

const sx = {
  card: {
    bgcolor: "#FFFFFF",
    borderRadius: "4px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.04)",
    p: 3,
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 500,
    color: "#262626",
  },
  addBtn: {
    width: 36,
    height: 36,
    color: "primary.main",
    borderRadius: "50%",
    "& .MuiSvgIcon-root": { fontSize: 20 },
    "&:hover": {
      bgcolor: "#E3F2FD",
    },
  },
  dashedBtn: {
    border: "1.5px dashed #CCCCCC",
    borderRadius: "8px",
    py: "32px",
    px: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "8px",
    bgcolor: "#fcfcfc",
    cursor: "pointer",
    transition: "all 150ms",
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: "#E8F1FE",
      "& .dashed-icon": {
        borderColor: "primary.main",
        color: "primary.main",
      },
    },
  },
  dashedIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    bgcolor: "white",
    border: "1.5px solid #CCCCCC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#7C7C7C",
    transition: "all 150ms",
  },
  dashedTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: "#262626",
    mt: "4px",
  },
  dashedSub: {
    fontSize: 13,
    color: "#7C7C7C",
    maxWidth: 380,
    lineHeight: 1.45,
  },
  sourceList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  sourceRow: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "8px",
    p: 2,
  },
  sourceHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 1,
  },
  sourceName: {
    fontSize: 15,
    fontWeight: 500,
    color: "#262626",
  },
  chipList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
  },
  chip: {
    fontSize: 12,
    color: COLORS.GREY_700,
    background: "#F5F5F5",
    border: "1px solid #E0E0E0",
    borderRadius: "12px",
    padding: "3px 10px",
    lineHeight: 1.3,
  },
}

// TODO: remove this constant when the delete feature is enabled in the backend
const DISABLE_DELETE = true

const RecipeCatalogSection = () => {
  const dispatch = useDispatch()
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const allEnterprises = useSelector(getOrganizationsEnterprisesSelector)
  const catalogProductTypes = useSelector(getCatalogProductTypesSelector)
  const [modalOpen, setModalOpen] = useState(false)

  if (!enterprise) return null

  const sources = enterprise.catalogSources || []
  const isEmpty = sources.length === 0

  const getSourceLabel = (source) => {
    if (source.sourceType === "BRAND") {
      const brand = ACTIVE_KFC_BRANDS.find(b => b.name === source.source)
      return brand ? brand.label : source.source
    }
    const sourceEnterprise = allEnterprises.find(e => e.objectId === source.source)
    return sourceEnterprise ? sourceEnterprise.name : "Entreprise inconnue"
  }

  const getProductTypeLabel = (kfcName) => {
    const match = catalogProductTypes.find(pt => pt.kfcName === kfcName)
    return match ? match.label : kfcName
  }

  return (
    <Box sx={sx.card}>
      <Box sx={sx.cardHead}>
        <Typography sx={sx.cardTitle}>Catalogue de recettes</Typography>
        {!isEmpty && (
          <IconButton
            size="small"
            sx={sx.addBtn}
            onClick={() => setModalOpen(true)}
            aria-label="Ajouter une source"
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {isEmpty ? (
        <Box
          sx={sx.dashedBtn}
          onClick={() => setModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setModalOpen(true)}
        >
          <Box sx={sx.dashedIcon} className="dashed-icon">
            <AddIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={sx.dashedTitle}>Générer le catalogue de recettes</Typography>
          <Typography sx={sx.dashedSub}>
            Sélectionnez une marque ou une entreprise source pour amorcer le catalogue
          </Typography>
        </Box>
      ) : (
        <Box sx={sx.sourceList}>
          {sources.map((source, index) => (
            <Box key={`${source.sourceType}-${source.source}-${index}`} sx={sx.sourceRow}>
              <Box sx={sx.sourceHead}>
                <Typography sx={sx.sourceName}>{getSourceLabel(source)}</Typography>
                {!DISABLE_DELETE && (
                  <IconButton
                    size="small"
                    onClick={() => dispatch(removeCatalogSource(enterprise.objectId, source.sourceType, source.source))}
                    aria-label="Retirer cette source"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={sx.chipList}>
                {source.productTypes.map((productType) => (
                  <Box key={productType} component="span" sx={sx.chip}>
                    {getProductTypeLabel(productType)}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <CatalogSourceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        enterpriseId={enterprise.objectId}
        existingSources={sources}
      />
    </Box>
  )
}

export default RecipeCatalogSection
